import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import analyticsService from '../../services/analyticsService';

// Async thunk for fetching homepage statistics
export const fetchHomePageStatistics = createAsyncThunk(
  'analytics/fetchHomePageStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const data = await analyticsService.getHomePageStatistics();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching user analytics
export const fetchUserAnalytics = createAsyncThunk(
  'analytics/fetchUserAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const data = await analyticsService.getUserAnalytics();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching order analytics
export const fetchOrderAnalytics = createAsyncThunk(
  'analytics/fetchOrderAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const data = await analyticsService.getOrderAnalytics();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching payment analytics
export const fetchPaymentAnalytics = createAsyncThunk(
  'analytics/fetchPaymentAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const data = await analyticsService.getPaymentAnalytics();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for generating sales report
export const generateSalesReport = createAsyncThunk(
  'analytics/generateSalesReport',
  async (params, { rejectWithValue }) => {
    try {
      const data = await analyticsService.generateSalesReport(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for generating product report
export const generateProductReport = createAsyncThunk(
  'analytics/generateProductReport',
  async (params, { rejectWithValue }) => {
    try {
      const data = await analyticsService.generateProductReport(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for generating user report
export const generateUserReport = createAsyncThunk(
  'analytics/generateUserReport',
  async (params, { rejectWithValue }) => {
    try {
      const data = await analyticsService.generateUserReport(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  homePageStats: {
    users: null,
    orders: null,
    payments: null
  },
  userAnalytics: null,
  orderAnalytics: null,
  paymentAnalytics: null,
  reports: {
    sales: null,
    products: null,
    users: null
  },
  loading: false,
  reportLoading: false,
  error: null
};

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetAnalytics: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch homepage statistics
      .addCase(fetchHomePageStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHomePageStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.homePageStats = action.payload;
        state.error = null;
      })
      .addCase(fetchHomePageStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch user analytics
      .addCase(fetchUserAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.userAnalytics = action.payload;
        state.error = null;
      })
      .addCase(fetchUserAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch order analytics
      .addCase(fetchOrderAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.orderAnalytics = action.payload;
        state.error = null;
      })
      .addCase(fetchOrderAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch payment analytics
      .addCase(fetchPaymentAnalytics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentAnalytics.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentAnalytics = action.payload;
        state.error = null;
      })
      .addCase(fetchPaymentAnalytics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Generate sales report
      .addCase(generateSalesReport.pending, (state) => {
        state.reportLoading = true;
        state.error = null;
      })
      .addCase(generateSalesReport.fulfilled, (state, action) => {
        state.reportLoading = false;
        state.reports.sales = action.payload;
        state.error = null;
      })
      .addCase(generateSalesReport.rejected, (state, action) => {
        state.reportLoading = false;
        state.error = action.payload;
      })
      // Generate product report
      .addCase(generateProductReport.pending, (state) => {
        state.reportLoading = true;
        state.error = null;
      })
      .addCase(generateProductReport.fulfilled, (state, action) => {
        state.reportLoading = false;
        state.reports.products = action.payload;
        state.error = null;
      })
      .addCase(generateProductReport.rejected, (state, action) => {
        state.reportLoading = false;
        state.error = action.payload;
      })
      // Generate user report
      .addCase(generateUserReport.pending, (state) => {
        state.reportLoading = true;
        state.error = null;
      })
      .addCase(generateUserReport.fulfilled, (state, action) => {
        state.reportLoading = false;
        state.reports.users = action.payload;
        state.error = null;
      })
      .addCase(generateUserReport.rejected, (state, action) => {
        state.reportLoading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, resetAnalytics } = analyticsSlice.actions;
export default analyticsSlice.reducer;